import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Truck, Wrench, Eye, RotateCw } from 'lucide-react';
import { motion } from 'framer-motion';

const ListingCard = ({ listing, index = 0, compact = false }) => {
  const mainImage = listing.images?.[0] || listing.after_images?.[0] || 'https://images.unsplash.com/photo-1527854176090-66db22836da9?w=400';
  const isSold = listing.status === 'sold';
  const isWorkshop = listing.listing_type === 'hands_on_workshop' || listing.listing_type === 'look_and_learn';

  // Compact mode: image-only tile with price overlay (used for ultra-dense 16-tile grid)
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index, 12) * 0.02, duration: 0.2 }}
      >
        <Link to={`/listing/${listing.id}`} data-testid={`listing-card-${listing.id}`}>
          <div className="bg-white border border-stone-200 overflow-hidden group relative aspect-square">
            <img
              src={mainImage}
              alt={listing.title}
              className={`w-full h-full object-cover ${isSold ? 'opacity-60' : ''}`}
              loading="lazy"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1527854176090-66db22836da9?w=200';
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-1">
              <span className="text-white text-[10px] font-semibold leading-none">${listing.price.toFixed(0)}</span>
            </div>
            {isWorkshop && (
              <span className="absolute top-1 right-1 px-1 py-0.5 bg-white/90 text-[8px] font-bold rounded text-purple-700">
                {listing.listing_type === 'look_and_learn' ? 'L&L' : 'WS'}
              </span>
            )}
            {isSold && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="bg-stone-900/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">SOLD</span>
              </div>
            )}
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      <Link to={`/listing/${listing.id}`} data-testid={`listing-card-${listing.id}`}>
        <div className="bg-white border border-stone-200 card-hover overflow-hidden group">
          <div className="aspect-[4/3] overflow-hidden relative">
            <img
              src={mainImage}
              alt={listing.title}
              className={`gallery-image ${isSold ? 'opacity-80' : ''}`}
              loading="lazy"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1527854176090-66db22836da9?w=400';
              }}
            />
            {/* SOLD Banner */}
            {isSold && (
              <div className="absolute top-0 left-0 w-full h-full flex items-start justify-start pointer-events-none">
                <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white font-bold text-sm px-4 py-1 transform -rotate-45 -translate-x-6 translate-y-4 shadow-lg"
                     style={{ width: '120px', textAlign: 'center' }}>
                  SOLD
                </div>
              </div>
            )}
            {listing.is_restoration && !isSold && (
              <span className="absolute top-2 left-2 badge-primary">
                Restored
              </span>
            )}
            {listing.listing_type === 'hands_on_workshop' && !isSold && (
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 bg-purple-600 text-white text-xs font-semibold rounded-full shadow">
                {listing.is_recurring && <RotateCw className="h-3 w-3" />}
                <Wrench className="h-3 w-3" /> Workshop
              </span>
            )}
            {listing.listing_type === 'look_and_learn' && !isSold && (
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-600 text-white text-xs font-semibold rounded-full shadow">
                {listing.is_recurring && <RotateCw className="h-3 w-3" />}
                <Eye className="h-3 w-3" /> Look & Learn
              </span>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className={`font-semibold line-clamp-1 group-hover:text-teal-600 transition-colors ${isSold ? 'text-stone-500' : 'text-stone-900'}`}>
                {listing.title}
              </h3>
              <span className={`price-tag text-lg whitespace-nowrap ${isSold ? 'text-stone-400' : ''}`}>
                ${listing.price.toFixed(0)}
              </span>
            </div>
            <p className="text-sm text-stone-500 mt-1 line-clamp-2">
              {listing.description}
            </p>
            <div className="flex items-center gap-1 mt-3 text-xs text-stone-400">
              <MapPin className="h-3 w-3" />
              <span>{listing.location}{listing.area ? ` • ${listing.area}` : ''}</span>
              {listing.offers_delivery && (
                <span className="ml-auto inline-flex items-center gap-1 text-green-600">
                  <Truck className="h-3 w-3" />
                  <span>Delivery</span>
                </span>
              )}
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-stone-400 capitalize">
                {listing.condition?.replace('-', ' ')}
              </span>
              {isSold ? (
                <span className="text-xs font-semibold text-teal-600">
                  SOLD
                </span>
              ) : (
                <span className="text-xs text-stone-400">
                  {listing.category?.replace('-', ' ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ListingCard;
